'use client';

import { useMemo, useCallback } from 'react';
import { createWalletClient, custom, encodeAbiParameters, parseUnits, encodeFunctionData, Hex, parseEventLogs } from 'viem';
import { useWallets } from '@privy-io/react-auth';
import { createFlaunch } from '@flaunch/sdk';
import { clientChain as chain, clientPublicClient } from '@/lib/viem-client';

// Flaunch ERC721 contract — the NFT minted when a coin is created
const FLAUNCH_NFT_ADDRESS = process.env.NEXT_PUBLIC_FLAUNCH_NFT_ADDRESS as `0x${string}` | undefined;

// USDC address
const USDC_ADDRESS = process.env.NEXT_PUBLIC_USDC_ADDRESS as `0x${string}` | undefined;

const TAKEOVER_ZAP_ADDRESS = process.env.NEXT_PUBLIC_TAKEOVER_ZAP_ADDRESS as `0x${string}` | undefined;

const FLAUNCH_ZAP_ADDRESS = process.env.NEXT_PUBLIC_FLAUNCH_ZAP_ADDRESS as `0x${string}` | undefined;

const GRID_LAUNCH_ZAP_ADDRESS = process.env.NEXT_PUBLIC_V2_GRID_LAUNCH_ZAP_ADDRESS as `0x${string}` | undefined;


// FlaunchZap ABI — 3-arg flaunch(FlaunchParams, address, bytes) overload
const FLAUNCH_ZAP_ABI = [
  {
    name: 'flaunch',
    type: 'function',
    stateMutability: 'payable',
    inputs: [
      {
        name: '_flaunchParams',
        type: 'tuple',
        components: [
          { name: 'name', type: 'string' },
          { name: 'symbol', type: 'string' },
          { name: 'tokenUri', type: 'string' },
          { name: 'initialTokenFairLaunch', type: 'uint256' },
          { name: 'fairLaunchDuration', type: 'uint256' },
          { name: 'premineAmount', type: 'uint256' },
          { name: 'creator', type: 'address' },
          { name: 'creatorFeeAllocation', type: 'uint24' },
          { name: 'flaunchAt', type: 'uint256' },
          { name: 'initialPriceParams', type: 'bytes' },
          { name: 'feeCalculatorParams', type: 'bytes' },
        ],
      },
      { name: '_trustedFeeSigner', type: 'address' },
      { name: '_premineSwapHookData', type: 'bytes' },
    ],
    outputs: [
      { name: 'memecoin_', type: 'address' },
      { name: 'ethSpent_', type: 'uint256' },
      { name: '', type: 'address' },
    ],
  },
] as const;

// TakeoverZap ABI - matches the contract's importWithSeats function
const TAKEOVER_ZAP_ABI = [
  {
    name: 'importWithSeats',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: '_flaunch', type: 'address' },
      { name: '_tokenId', type: 'uint256' },
      {
        name: '_seatPreBuys',
        type: 'tuple[]',
        components: [
          { name: 'seatId', type: 'uint256' },
          { name: 'selfAssessedPrice', type: 'uint256' },
          { name: 'deposit', type: 'uint256' },
        ],
      },
    ],
    outputs: [],
  },
] as const;

const GRID_LAUNCH_ZAP_ABI = [
  {
    name: 'launchFlaunchGrid',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'flaunch', type: 'address' },
      { name: 'tokenId', type: 'uint256' },
      {
        name: 'gridParams',
        type: 'tuple',
        components: [
          { name: 'taxRateBps', type: 'uint16' },
          { name: 'taxToken', type: 'address' },
          { name: 'yieldToken', type: 'address' },
          { name: 'tilePrices', type: 'uint128[]' },
        ],
      },
    ],
    outputs: [{ name: 'gridId', type: 'uint256' }],
  },
  {
    name: 'launchV3Grid',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'positionManager', type: 'address' },
      { name: 'tokenId', type: 'uint256' },
      {
        name: 'gridParams',
        type: 'tuple',
        components: [
          { name: 'taxRateBps', type: 'uint16' },
          { name: 'taxToken', type: 'address' },
          { name: 'yieldToken', type: 'address' },
          { name: 'tilePrices', type: 'uint128[]' },
        ],
      },
    ],
    outputs: [{ name: 'gridId', type: 'uint256' }],
  },
  {
    name: 'launchV4Grid',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'tokenId', type: 'uint256' },
      {
        name: 'gridParams',
        type: 'tuple',
        components: [
          { name: 'taxRateBps', type: 'uint16' },
          { name: 'taxToken', type: 'address' },
          { name: 'yieldToken', type: 'address' },
          { name: 'tilePrices', type: 'uint128[]' },
        ],
      },
    ],
    outputs: [{ name: 'gridId', type: 'uint256' }],
  },
  {
    name: 'createGridWithLiquidity',
    type: 'function',
    stateMutability: 'payable',
    inputs: [
      { name: 'token0', type: 'address' },
      { name: 'token1', type: 'address' },
      { name: 'amount0', type: 'uint256' },
      { name: 'amount1', type: 'uint256' },
      {
        name: 'gridParams',
        type: 'tuple',
        components: [
          { name: 'taxRateBps', type: 'uint16' },
          { name: 'taxToken', type: 'address' },
          { name: 'yieldToken', type: 'address' },
          { name: 'tilePrices', type: 'uint128[]' },
        ],
      },
    ],
    outputs: [{ name: 'gridId', type: 'uint256' }],
  },
] as const;

const GRID_CREATED_EVENT_ABI = [
  {
    name: 'GridCreated',
    type: 'event',
    inputs: [
      { name: 'gridId', type: 'uint256', indexed: true },
      { name: 'creator', type: 'address', indexed: true },
    ],
  },
] as const;

/**
 * Parameters for pre-buying seats via TakeoverZap (import existing NFT flow)
 */
export interface SeatPreBuy {
  seatId: number;
  /** Self-assessed price in USDC (human readable, e.g. "100" for $100) */
  priceUsdc: string;
  /** Deposit amount in USDC (human readable, e.g. "5" for $5) */
  depositUsdc: string;
}

export interface LaunchResult {
  hash: `0x${string}`;
  coinAddress?: `0x${string}`;
  poolId?: `0x${string}`;
}

/**
 * Creates a Privy-compatible transport that maps wallet_sendTransaction to eth_sendTransaction
 */
function createPrivyCompatibleTransport(provider: { request: (args: { method: string; params?: unknown[] }) => Promise<unknown> }) {
  return custom({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async request({ method, params }: { method: string; params?: any }) {
      // Map wallet_sendTransaction to eth_sendTransaction for Privy compatibility
      const actualMethod = method === 'wallet_sendTransaction' ? 'eth_sendTransaction' : method;
      return provider.request({ method: actualMethod, params });
    },
  });
}

export function useFlaunch() {
  const { wallets } = useWallets();

  // Get the primary wallet - prefer external wallets over embedded for better compatibility
  const wallet = useMemo(() => {
    if (!wallets.length) return null;
    // Prefer external wallet (MetaMask, etc.) for better RPC compatibility
    const external = wallets.find(w => w.walletClientType !== 'privy');
    if (external) return external;
    // Fall back to embedded wallet
    const embedded = wallets.find(w => w.walletClientType === 'privy');
    return embedded || wallets[0];
  }, [wallets]);

  // Reuse the shared client-side viem client (batched multicall transport) so
  // Flaunch reads join the same RPC connection pool as the rest of the app.
  const publicClient = clientPublicClient;

  // Get wallet client from Privy wallet
  const getWalletClient = useCallback(async () => {
    if (!wallet) return null;

    try {
      // Switch to correct chain first
      await wallet.switchChain(chain.id);

      // Get the EIP1193 provider from Privy
      const provider = await wallet.getEthereumProvider();

      // Use custom transport for Privy embedded wallet to handle method mapping
      const transport = wallet.walletClientType === 'privy'
        ? createPrivyCompatibleTransport(provider)
        : custom(provider);

      const walletClient = createWalletClient({
        chain,
        transport,
        account: wallet.address as `0x${string}`,
      });

      return walletClient;
    } catch (error) {
      console.error('Failed to get wallet client:', error);
      return null;
    }
  }, [wallet]);

  // Create flaunch SDK instance for write operations
  const getFlaunchWrite = useCallback(async () => {
    const walletClient = await getWalletClient();
    if (!walletClient) return null;

    // Use type assertion to handle viem version mismatches
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return createFlaunch({
      publicClient: publicClient as any,
      walletClient: walletClient as any,
    });
  }, [publicClient, getWalletClient]);

  // Get USDC address for external use
  const getAddresses = useCallback(() => ({
    usdcAddress: USDC_ADDRESS,
  }), []);

  // Upload image + metadata to our own IPFS via the server-side API route.
  // Returns both the tokenUri (for on-chain) and the resolved imageUrl (for DB storage).
  const uploadToIPFS = useCallback(async (params: {
    name: string;
    symbol: string;
    base64Image: string;
    description?: string;
  }): Promise<{
    tokenUri: string;
    imageUrl: string;
    nsfwFlagged?: boolean;
    originalImageIpfsHash?: string;
  }> => {
    const response = await fetch('/api/ipfs/upload', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: params.name,
        symbol: params.symbol,
        base64Image: params.base64Image,
        description: params.description,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[IPFS] Upload failed:', response.status, errorText);
      throw new Error(`Failed to upload to IPFS: ${response.status} ${errorText}`);
    }

    const result = await response.json();

    return {
      tokenUri: result.tokenUri,
      imageUrl: result.imageUrl,
      nsfwFlagged: result.nsfwFlagged,
      originalImageIpfsHash: result.originalImageIpfsHash,
    };
  }, []);

  // Send launch transaction via FlaunchZap — creates a new Flaunch coin.
  // The NFT is minted to the caller; use GridLaunchZap afterwards to create a grid.
  const sendFlaunchTx = useCallback(async (params: {
    name: string;
    symbol: string;
    tokenUri: string;
    creatorFeePercent: number;
    initialMarketCapUSD?: number;
  }): Promise<{ hash: `0x${string}`; needsConfirmation: true }> => {
    if (!wallet?.address) {
      throw new Error('No wallet address');
    }

    if (!FLAUNCH_ZAP_ADDRESS) {
      throw new Error('FlaunchZap address not configured. Set NEXT_PUBLIC_FLAUNCH_ZAP_ADDRESS.');
    }

    const walletClient = await getWalletClient();
    if (!walletClient) {
      throw new Error('Wallet not connected');
    }

    const marketCapUsdc = BigInt(params.initialMarketCapUSD ?? 10_000) * BigInt(1e6);
    const initialPriceParams = encodeAbiParameters(
      [{ type: 'uint256' }],
      [marketCapUsdc],
    );

    // `Math.round` keeps the bps value as a clean integer for ABI encoding —
    // 100 → 10000 is already exact in JS floats, but a future caller passing
    // 99.99 would otherwise drift to 9998.999… and fail uint256 packing.
    const creatorFeeAllocation = Math.round(params.creatorFeePercent * 100);

    const flaunchParams = {
      name: params.name,
      symbol: params.symbol,
      tokenUri: params.tokenUri,
      initialTokenFairLaunch: 0n,
      fairLaunchDuration: 0n,
      premineAmount: 0n,
      creator: wallet.address as `0x${string}`,
      creatorFeeAllocation,
      flaunchAt: 0n,
      initialPriceParams,
      feeCalculatorParams: '0x' as Hex,
    };

    const flaunchFee = await publicClient.readContract({
      address: FLAUNCH_ZAP_ADDRESS,
      abi: [{
        name: 'calculateFee',
        type: 'function',
        stateMutability: 'view',
        inputs: [
          { name: '_premineAmount', type: 'uint256' },
          { name: '_slippage', type: 'uint256' },
          { name: '_initialPriceParams', type: 'bytes' },
        ],
        outputs: [{ name: 'ethRequired_', type: 'uint256' }],
      }],
      functionName: 'calculateFee',
      args: [0n, 0n, initialPriceParams],
    });

    const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000' as `0x${string}`;

    const calldata = encodeFunctionData({
      abi: FLAUNCH_ZAP_ABI,
      functionName: 'flaunch',
      args: [flaunchParams, ZERO_ADDRESS, '0x' as Hex],
    });

    const hash = await walletClient.sendTransaction({
      to: FLAUNCH_ZAP_ADDRESS,
      data: calldata,
      value: flaunchFee,
    });

    if (!hash || typeof hash !== 'string' || !hash.startsWith('0x')) {
      console.error('Invalid hash:', hash);
      throw new Error('Invalid transaction hash');
    }

    return { hash, needsConfirmation: true as const };
  }, [getWalletClient, wallet?.address, publicClient]);

  // Send import transaction via TakeoverZap — imports an existing Flaunch NFT
  // with atomic seat pre-buys in a single transaction.
  // User must have: 1) approved zap for NFT transfer, 2) approved zap for USDC spending
  const sendImportWithSeatsTx = useCallback(async (params: {
    flaunchAddress: `0x${string}`;
    tokenId: number;
    seatPreBuys?: SeatPreBuy[];
  }): Promise<{ hash: `0x${string}`; needsConfirmation: true }> => {
    if (!wallet?.address) {
      throw new Error('No wallet connected');
    }
    if (!TAKEOVER_ZAP_ADDRESS) {
      throw new Error('TakeoverZap address not configured. Set NEXT_PUBLIC_TAKEOVER_ZAP_ADDRESS.');
    }

    const walletClient = await getWalletClient();
    if (!walletClient) {
      throw new Error('Failed to get wallet client');
    }

    const seatPreBuys = params.seatPreBuys?.map(seat => ({
      seatId: BigInt(seat.seatId),
      selfAssessedPrice: parseUnits(seat.priceUsdc, 6),
      deposit: parseUnits(seat.depositUsdc, 6),
    })) || [];

    const calldata = encodeFunctionData({
      abi: TAKEOVER_ZAP_ABI,
      functionName: 'importWithSeats',
      args: [
        params.flaunchAddress,
        BigInt(params.tokenId),
        seatPreBuys,
      ],
    });

    const hash = await walletClient.sendTransaction({
      to: TAKEOVER_ZAP_ADDRESS,
      data: calldata,
      account: wallet.address as `0x${string}`,
      chain,
    });

    return { hash, needsConfirmation: true as const };
  }, [wallet?.address, getWalletClient]);

  // Build importWithSeats calldata without sending — for EIP-7702 batching
  const buildImportWithSeatsCall = useCallback((params: {
    flaunchAddress: `0x${string}`;
    tokenId: number;
    seatPreBuys?: SeatPreBuy[];
  }): { to: `0x${string}`; data: `0x${string}` } => {
    if (!TAKEOVER_ZAP_ADDRESS) {
      throw new Error('TakeoverZap address not configured. Set NEXT_PUBLIC_TAKEOVER_ZAP_ADDRESS.');
    }

    const seatPreBuys = params.seatPreBuys?.map(seat => ({
      seatId: BigInt(seat.seatId),
      selfAssessedPrice: parseUnits(seat.priceUsdc, 6),
      deposit: parseUnits(seat.depositUsdc, 6),
    })) || [];

    const calldata = encodeFunctionData({
      abi: TAKEOVER_ZAP_ABI,
      functionName: 'importWithSeats',
      args: [
        params.flaunchAddress,
        BigInt(params.tokenId),
        seatPreBuys,
      ],
    });

    return { to: TAKEOVER_ZAP_ADDRESS, data: calldata };
  }, []);

  // Read-only SDK for fetching coin data (must be defined before getPoolCreatedData)
  const flaunchRead = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return createFlaunch({ publicClient: publicClient as any });
  }, [publicClient]);

  // Parse a transaction hash to extract coin address, pool ID, and Flaunch NFT token ID
  const getPoolCreatedData = useCallback(async (txHash: `0x${string}`): Promise<{
    coinAddress?: `0x${string}`;
    poolId?: `0x${string}`;
    tokenId?: number;
  }> => {
    try {
      const poolData = await flaunchRead.getPoolCreatedFromTx(txHash);

      if (poolData) {
        return {
          coinAddress: poolData.memecoin as `0x${string}`,
          poolId: poolData.poolId as `0x${string}`,
          tokenId: Number(poolData.tokenId),
        };
      }
    } catch (error) {
      console.error('[getPoolCreatedData] Failed to parse PoolCreated event:', error);
    }

    return {};
  }, [flaunchRead]);

  const getGridCreatedData = useCallback(async (txHash: `0x${string}`): Promise<{
    gridId?: string;
  }> => {
    try {
      const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash });
      const events = parseEventLogs({
        abi: GRID_CREATED_EVENT_ABI,
        logs: receipt.logs,
      });
      if (events.length > 0) {
        return { gridId: events[0].args.gridId.toString() };
      }
    } catch (error) {
      console.error('[getGridCreatedData] Failed to parse GridCreated event:', error);
    }
    return {};
  }, [publicClient]);

  // ═══════════════════════════════════════════════════════════════════════════
  // Existing NFT Deposit Helpers
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Fetch metadata for an existing Flaunch NFT (name, symbol, image, owner, memecoin address).
   */
  const fetchNftMetadata = useCallback(async (flaunchAddress: `0x${string}`, tokenId: number): Promise<{
    name: string;
    symbol: string;
    imageUrl: string | null;
    owner: `0x${string}`;
    memecoinAddress: `0x${string}`;
  }> => {
    const { ERC721_ABI, FLAUNCH_ABI, MEMECOIN_ABI } = await import('@/lib/contracts');

    // Get NFT owner
    const owner = await publicClient.readContract({
      address: flaunchAddress,
      abi: ERC721_ABI,
      functionName: 'ownerOf',
      args: [BigInt(tokenId)],
    }) as `0x${string}`;

    // Get memecoin address from Flaunch
    const memecoinAddress = await publicClient.readContract({
      address: flaunchAddress,
      abi: FLAUNCH_ABI,
      functionName: 'memecoin',
      args: [BigInt(tokenId)],
    }) as `0x${string}`;

    // Read name + symbol from the memecoin contract
    const [name, symbol] = await Promise.all([
      publicClient.readContract({
        address: memecoinAddress,
        abi: MEMECOIN_ABI,
        functionName: 'name',
      }) as Promise<string>,
      publicClient.readContract({
        address: memecoinAddress,
        abi: MEMECOIN_ABI,
        functionName: 'symbol',
      }) as Promise<string>,
    ]);

    // Try to get image from tokenURI metadata
    let imageUrl: string | null = null;
    try {
      const tokenUri = await publicClient.readContract({
        address: flaunchAddress,
        abi: ERC721_ABI,
        functionName: 'tokenURI',
        args: [BigInt(tokenId)],
      }) as string;
      if (tokenUri) {
        const metadataUrl = tokenUri.startsWith('ipfs://')
          ? tokenUri.replace('ipfs://', 'https://ipfs.io/ipfs/')
          : tokenUri;
        const res = await fetch(metadataUrl);
        if (res.ok) {
          const metadata = await res.json();
          if (metadata.image) {
            imageUrl = metadata.image.startsWith('ipfs://')
              ? metadata.image.replace('ipfs://', 'https://ipfs.io/ipfs/')
              : metadata.image;
          }
        }
      }
    } catch {
      // Image fetch is best-effort
    }

    return { name, symbol, imageUrl, owner, memecoinAddress };
  }, [publicClient]);

  /**
   * Build calldata for ERC721.approve(zap, tokenId) on a Flaunch NFT.
   * The zap transfers the NFT to the manager on behalf of the user.
   */
  const buildNftApproveCall = useCallback((
    flaunchAddress: `0x${string}`,
    tokenId: number,
  ): { to: `0x${string}`; data: `0x${string}` } => {
    if (!TAKEOVER_ZAP_ADDRESS) throw new Error('TakeoverZap address not configured');
    const data = encodeFunctionData({
      abi: [{ name: 'approve', type: 'function', stateMutability: 'nonpayable', inputs: [{ name: 'to', type: 'address' }, { name: 'tokenId', type: 'uint256' }], outputs: [] }],
      functionName: 'approve',
      args: [TAKEOVER_ZAP_ADDRESS, BigInt(tokenId)],
    });
    return { to: flaunchAddress, data };
  }, []);

  /**
   * Check if the zap is already approved to transfer a specific NFT.
   */
  const checkNftApproval = useCallback(async (
    flaunchAddress: `0x${string}`,
    tokenId: number,
  ): Promise<boolean> => {
    if (!TAKEOVER_ZAP_ADDRESS) return false;
    const { ERC721_ABI } = await import('@/lib/contracts');
    const approved = await publicClient.readContract({
      address: flaunchAddress,
      abi: ERC721_ABI,
      functionName: 'getApproved',
      args: [BigInt(tokenId)],
    }) as `0x${string}`;
    return approved.toLowerCase() === TAKEOVER_ZAP_ADDRESS.toLowerCase();
  }, [publicClient]);

  /**
   * Build calldata for TakeoverManager.deposit(flaunchToken, creator, "0x").
   */
  const buildDepositCall = useCallback((
    flaunchAddress: `0x${string}`,
    tokenId: number,
    creator: `0x${string}`,
    managerAddress: `0x${string}`,
  ): { to: `0x${string}`; data: `0x${string}` } => {
    if (!managerAddress) throw new Error('Manager address is required');
    const depositAbi = [{
      name: 'deposit', type: 'function', stateMutability: 'nonpayable',
      inputs: [
        { name: '_flaunchToken', type: 'tuple', components: [
          { name: 'flaunch', type: 'address' },
          { name: 'tokenId', type: 'uint256' },
        ]},
        { name: '_creator', type: 'address' },
        { name: '_data', type: 'bytes' },
      ],
      outputs: [],
    }] as const;
    const data = encodeFunctionData({
      abi: depositAbi,
      functionName: 'deposit',
      args: [
        { flaunch: flaunchAddress, tokenId: BigInt(tokenId) },
        creator,
        '0x' as `0x${string}`,
      ],
    });
    return { to: managerAddress, data };
  }, []);

  /**
   * Build calldata for TakeoverManager.addBatch(poolId, seatParams).
   * Used after deposit to claim seats on the newly registered pool.
   */
  const buildAddBatchCall = useCallback((
    poolId: `0x${string}`,
    seats: Array<{ seatId: number; priceUsdc: string; depositUsdc: string }>,
    managerAddress: `0x${string}`,
  ): { to: `0x${string}`; data: `0x${string}` } => {
    if (!managerAddress) throw new Error('Manager address is required');
    const addBatchAbi = [{
      name: 'addBatch', type: 'function', stateMutability: 'nonpayable',
      inputs: [
        { name: '_poolId', type: 'bytes32' },
        { name: '_params', type: 'tuple[]', components: [
          { name: 'seatId', type: 'uint256' },
          { name: 'price', type: 'uint256' },
          { name: 'maxPrice', type: 'uint256' },
          { name: 'amount', type: 'uint256' },
        ]},
      ],
      outputs: [],
    }] as const;
    const seatParams = seats.map(s => ({
      seatId: BigInt(s.seatId),
      price: parseUnits(s.priceUsdc, 6),
      maxPrice: 0n,
      amount: parseUnits(s.depositUsdc, 6),
    }));
    const data = encodeFunctionData({
      abi: addBatchAbi,
      functionName: 'addBatch',
      args: [poolId, seatParams],
    });
    return { to: managerAddress, data };
  }, []);

  /**
   * Get the PoolId for a memecoin by querying the app's API (backed by indexer + metadata cache).
   * Retries a few times since the pool may not be indexed immediately after deposit.
   */
  const getPoolIdForMemecoin = useCallback(async (memecoinAddress: `0x${string}`): Promise<`0x${string}` | null> => {
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const res = await fetch(`/api/coin/${memecoinAddress}`);
        if (res.ok) {
          const data = await res.json();
          if (data.pool?.id) return data.pool.id as `0x${string}`;
        }
      } catch (error) {
        console.error(`[getPoolIdForMemecoin] Attempt ${attempt + 1} failed:`, error);
      }
      if (attempt < 4) await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
    }
    return null;
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // V2 Grid Launch (via GridLaunchZap)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Build ERC721.approve(GridLaunchZap, tokenId) calldata for any NFT.
   */
  const buildGridLaunchApproveCall = useCallback((
    nftAddress: `0x${string}`,
    tokenId: number,
  ): { to: `0x${string}`; data: `0x${string}` } => {
    if (!GRID_LAUNCH_ZAP_ADDRESS) throw new Error('GridLaunchZap address not configured');
    const data = encodeFunctionData({
      abi: [{ name: 'approve', type: 'function', stateMutability: 'nonpayable', inputs: [{ name: 'to', type: 'address' }, { name: 'tokenId', type: 'uint256' }], outputs: [] }],
      functionName: 'approve',
      args: [GRID_LAUNCH_ZAP_ADDRESS, BigInt(tokenId)],
    });
    return { to: nftAddress, data };
  }, []);

  /**
   * Check if GridLaunchZap is approved to transfer a specific NFT.
   */
  const checkGridLaunchApproval = useCallback(async (
    nftAddress: `0x${string}`,
    tokenId: number,
  ): Promise<boolean> => {
    if (!GRID_LAUNCH_ZAP_ADDRESS) return false;
    const approved = await publicClient.readContract({
      address: nftAddress,
      abi: [{ name: 'getApproved', type: 'function', stateMutability: 'view', inputs: [{ name: 'tokenId', type: 'uint256' }], outputs: [{ name: '', type: 'address' }] }],
      functionName: 'getApproved',
      args: [BigInt(tokenId)],
    }) as `0x${string}`;
    return approved.toLowerCase() === GRID_LAUNCH_ZAP_ADDRESS.toLowerCase();
  }, [publicClient]);

  /**
   * Build calldata for GridLaunchZap.launchFlaunchGrid() — used for ERC-5792 batching.
   */
  const buildLaunchFlaunchGridCall = useCallback((params: {
    flaunchAddress: `0x${string}`;
    tokenId: number;
    tilePriceUsdc: string;
    taxToken?: `0x${string}`;
    yieldToken?: `0x${string}`;
  }): { to: `0x${string}`; data: `0x${string}` } => {
    if (!GRID_LAUNCH_ZAP_ADDRESS) throw new Error('GridLaunchZap address not configured');

    const tilePrice = parseUnits(params.tilePriceUsdc, 6);
    const tilePrices = Array.from({ length: 100 }, () => tilePrice);
    const taxToken = params.taxToken ?? USDC_ADDRESS!;
    const yieldToken = params.yieldToken ?? '0x0000000000000000000000000000000000000000';

    const data = encodeFunctionData({
      abi: GRID_LAUNCH_ZAP_ABI,
      functionName: 'launchFlaunchGrid',
      args: [
        params.flaunchAddress,
        BigInt(params.tokenId),
        { taxRateBps: 0, taxToken, yieldToken, tilePrices },
      ],
    });

    return { to: GRID_LAUNCH_ZAP_ADDRESS, data };
  }, []);

  /**
   * Build calldata for GridLaunchZap.launchV3Grid() — used for ERC-5792 batching.
   */
  const buildLaunchV3GridCall = useCallback((params: {
    positionManager: `0x${string}`;
    tokenId: number;
    tilePriceUsdc: string;
    taxToken?: `0x${string}`;
    yieldToken?: `0x${string}`;
  }): { to: `0x${string}`; data: `0x${string}` } => {
    if (!GRID_LAUNCH_ZAP_ADDRESS) throw new Error('GridLaunchZap address not configured');

    const tilePrice = parseUnits(params.tilePriceUsdc, 6);
    const tilePrices = Array.from({ length: 100 }, () => tilePrice);
    const taxToken = params.taxToken ?? USDC_ADDRESS!;
    const yieldToken = params.yieldToken ?? '0x0000000000000000000000000000000000000000';

    const data = encodeFunctionData({
      abi: GRID_LAUNCH_ZAP_ABI,
      functionName: 'launchV3Grid',
      args: [
        params.positionManager,
        BigInt(params.tokenId),
        { taxRateBps: 0, taxToken, yieldToken, tilePrices },
      ],
    });

    return { to: GRID_LAUNCH_ZAP_ADDRESS, data };
  }, []);

  /**
   * Build calldata for GridLaunchZap.launchV4Grid() — used for ERC-5792 batching.
   */
  const buildLaunchV4GridCall = useCallback((params: {
    tokenId: number;
    tilePriceUsdc: string;
    taxToken?: `0x${string}`;
    yieldToken?: `0x${string}`;
  }): { to: `0x${string}`; data: `0x${string}` } => {
    if (!GRID_LAUNCH_ZAP_ADDRESS) throw new Error('GridLaunchZap address not configured');

    const tilePrice = parseUnits(params.tilePriceUsdc, 6);
    const tilePrices = Array.from({ length: 100 }, () => tilePrice);
    const taxToken = params.taxToken ?? USDC_ADDRESS!;
    const yieldToken = params.yieldToken ?? '0x0000000000000000000000000000000000000000';

    const data = encodeFunctionData({
      abi: GRID_LAUNCH_ZAP_ABI,
      functionName: 'launchV4Grid',
      args: [
        BigInt(params.tokenId),
        { taxRateBps: 0, taxToken, yieldToken, tilePrices },
      ],
    });

    return { to: GRID_LAUNCH_ZAP_ADDRESS, data };
  }, []);

  /**
   * Build calldata for GridLaunchZap.createGridWithLiquidity() --
   * atomically creates a Uniswap V4 LP position from two tokens and launches a grid.
   * If one of the tokens is ETH (address(0)), msg.value must include that amount.
   */
  const buildCreateGridWithLiquidityCall = useCallback((params: {
    token0: `0x${string}`;
    token1: `0x${string}`;
    amount0: bigint;
    amount1: bigint;
    tilePriceUsdc: string;
    taxToken?: `0x${string}`;
    yieldToken?: `0x${string}`;
  }): { to: `0x${string}`; data: `0x${string}`; value: bigint } => {
    if (!GRID_LAUNCH_ZAP_ADDRESS) throw new Error('GridLaunchZap address not configured. Set NEXT_PUBLIC_V2_GRID_LAUNCH_ZAP_ADDRESS.');

    const tilePrice = parseUnits(params.tilePriceUsdc, 6);
    const tilePrices = Array.from({ length: 100 }, () => tilePrice);
    const taxToken = params.taxToken ?? USDC_ADDRESS!;
    const yieldToken = params.yieldToken ?? '0x0000000000000000000000000000000000000000';

    const ZERO = '0x0000000000000000000000000000000000000000' as `0x${string}`;

    let t0 = params.token0;
    let t1 = params.token1;
    let a0 = params.amount0;
    let a1 = params.amount1;
    const shouldSwap =
      (t1 === ZERO) ||
      (t0 !== ZERO && t1 !== ZERO && t0.toLowerCase() > t1.toLowerCase());
    if (shouldSwap) {
      [t0, t1] = [t1, t0];
      [a0, a1] = [a1, a0];
    }

    const ethValue = t0 === ZERO ? a0 : 0n;

    const data = encodeFunctionData({
      abi: GRID_LAUNCH_ZAP_ABI,
      functionName: 'createGridWithLiquidity',
      args: [
        t0,
        t1,
        a0,
        a1,
        { taxRateBps: 0, taxToken, yieldToken, tilePrices },
      ],
    });

    return { to: GRID_LAUNCH_ZAP_ADDRESS, data, value: ethValue };
  }, []);

  /**
   * Build ERC20.approve(spender, amount) calldata.
   */
  const buildErc20ApproveCall = useCallback((
    tokenAddress: `0x${string}`,
    spender: `0x${string}`,
    amount: bigint,
  ): { to: `0x${string}`; data: `0x${string}` } => {
    const data = encodeFunctionData({
      abi: [{ name: 'approve', type: 'function', stateMutability: 'nonpayable', inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }], outputs: [{ name: '', type: 'bool' }] }],
      functionName: 'approve',
      args: [spender, amount],
    });
    return { to: tokenAddress, data };
  }, []);

  /**
   * Check ERC20 allowance for a spender.
   */
  const checkErc20Allowance = useCallback(async (
    tokenAddress: `0x${string}`,
    owner: `0x${string}`,
    spender: `0x${string}`,
  ): Promise<bigint> => {
    const result = await publicClient.readContract({
      address: tokenAddress,
      abi: [{ name: 'allowance', type: 'function', stateMutability: 'view', inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }], outputs: [{ name: '', type: 'uint256' }] }],
      functionName: 'allowance',
      args: [owner, spender],
    });
    return result as bigint;
  }, [publicClient]);

  return {
    isReady: !!wallet,
    // Launch flow
    uploadToIPFS,
    sendFlaunchTx,
    getPoolCreatedData,
    getGridCreatedData,
    // Import existing NFT flow
    sendImportWithSeatsTx,
    buildImportWithSeatsCall,
    // NFT helpers
    fetchNftMetadata,
    buildNftApproveCall,
    checkNftApproval,
    buildDepositCall,
    buildAddBatchCall,
    getPoolIdForMemecoin,
    // V2 Grid Launch
    buildGridLaunchApproveCall,
    checkGridLaunchApproval,
    buildLaunchFlaunchGridCall,
    buildLaunchV3GridCall,
    buildLaunchV4GridCall,
    buildCreateGridWithLiquidityCall,
    buildErc20ApproveCall,
    checkErc20Allowance,
    gridLaunchZapAddress: GRID_LAUNCH_ZAP_ADDRESS,
    // Addresses & clients
    getAddresses,
    flaunchRead,
    getFlaunchWrite,
    flaunchNftAddress: FLAUNCH_NFT_ADDRESS,
    usdcAddress: USDC_ADDRESS,
    flaunchZapAddress: FLAUNCH_ZAP_ADDRESS,
    takeoverZapAddress: TAKEOVER_ZAP_ADDRESS,
    publicClient,
  };
}
